import Calendar20 from '@carbon/icons-react/es/calendar/20';
import Enterprise24 from '@carbon/icons-react/es/enterprise/24';
import Task16 from '@carbon/icons-react/es/task/16';
import Task20 from '@carbon/icons-react/es/task/20';
import { Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ReactComponent as ActivityIcon } from 'assets/svgs/activity.svg';
import { ReactComponent as MilestoneIcon } from 'assets/svgs/milestone-black.svg';
import { ReactComponent as PhaseIcon } from 'assets/svgs/phase-icon.svg';
import { ReactComponent as TodoIcon } from 'assets/svgs/to-do.svg';
import { Avatar } from 'components/atoms/avatar';
import { RelativeTime } from 'components/atoms/relative-time';
import { SingleLineEllipsis } from 'components/styles';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { useProjectDetailQuery } from 'queries/project';
import { fullName } from 'utils/full-name';
import { NameCellWithTooltip } from '../../common/cells/name-cell';
import { NoteSubType, RejectNoteType, WorkType } from '../../types';
import styles from './user-info-action.module.css';

const { Paragraph } = Typography;

export const TextWrapperWithEllipsis = styled.div`
  ${SingleLineEllipsis};
  font-size: var(--font-size-sm);
`;

export const SecondaryTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  padding-left: var(--spacing-06);
`;

const NameCellWithTooltipSTyled = styled(NameCellWithTooltip)`
  height: calc(var(--spacing-09) + var(--spacing-03));
  display: flex;
  align-items: center;
`;

const WorkNameWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const WorkIconWrapper = styled.span`
  width: var(--heading-5-size);
  height: var(--heading-5-size);
  margin-right: var(--spacing-02);
`;

const StatusIndicatorWrapper = styled.span<{ subTitle?: boolean }>`
  svg {
    width: ${({ subTitle }) =>
      subTitle ? 'var(--spacing-05)' : 'var(--spacing-06)'};
    height: ${({ subTitle }) =>
      subTitle ? 'var(--spacing-05)' : 'var(--spacing-06)'};
  }
`;

const NoteWrapper = styled.div`
  width: 328px;
  background: #ffffff;
  padding: var(--spacing-05);
  margin: var(--spacing-04) 0;
  box-shadow: var(--shadow-02);
`;

const NoteTypeWrapper = styled.div<{ workCell?: boolean }>`
  width: 296px;
  background: #ffffff;
  box-shadow: var(--shadow-02);
  margin-bottom: var(--spacing-05);
  padding: ${({ workCell }) =>
    workCell && 'var(--spacing-03) var(--spacing-05)'};
`;

const ParagraphStyled = styled(Paragraph)`
  margin-bottom: 0;
  padding-left: var(--spacing-07);

  span {
    font-style: italic;
    color: var(--gray-70);
    display: block;
    width: 90%;
  }

  p {
    margin-bottom: 0;
  }
`;

const CommentWrapper = styled.div`
  margin-bottom: 0;
`;

const WorkIcon = ({ workType }: { workType: WorkType }) => {
  switch (workType) {
    case WorkType.Milestone:
      return <MilestoneIcon />;

    case WorkType.Task:
      return <TodoIcon />;

    case WorkType.Phase:
      return <PhaseIcon />;

    case WorkType.GoogleCalendar:
    case WorkType.OutlookCalendar:
      return <Calendar20 />;

    case WorkType.Project:
      return <Task20 />;

    default:
      return <ActivityIcon />;
  }
};

export const RejectNote = ({ note }: { note: RejectNoteType }) => {
  const { data: project } = useProjectDetailQuery(
    ['project_id', Number(note.projectId)],
    {
      enabled:
        note.subType === NoteSubType.ProjectReject ||
        note.subType === NoteSubType.WorkReject,
    },
  );

  const user = useGetUserFromIndexedDb(note.createdBy);

  return (
    <NoteWrapper>
      {note.subType === NoteSubType.ProjectReject && (
        <NoteTypeWrapper>
          <NameCellWithTooltipSTyled
            logo={
              project?.projectId ? (
                <StatusIndicatorWrapper>
                  <Avatar
                    src={project?.customer.companyLogoUrl}
                    name={project?.projectName}
                    icon={<Task16 />}
                    size="small"
                  />
                </StatusIndicatorWrapper>
              ) : (
                <Enterprise24 />
              )
            }
            primaryText={
              project?.projectName ? (
                project?.projectName
              ) : (
                <FormattedMessage id="timesheets.labels.non_project_activity" />
              )
            }
          />
        </NoteTypeWrapper>
      )}
      {note.subType === NoteSubType.WorkReject && (
        <NoteTypeWrapper workCell>
          <WorkNameWrapper>
            <WorkIconWrapper>
              <WorkIcon workType={note.workType} />
            </WorkIconWrapper>
            {note.workType === WorkType.Project ? (
              <span>
                <FormattedMessage id="timesheets.reported.on.project" />
              </span>
            ) : (
              <span>{note.workName}</span>
            )}
          </WorkNameWrapper>
          <SecondaryTextWrapper>
            <Avatar
              size={12}
              src={project?.customer.companyLogoUrl}
              name={project?.projectName}
              icon={<Task16 />}
            />
            <TextWrapperWithEllipsis>
              {project?.projectName ? (
                project?.projectName
              ) : (
                <FormattedMessage id="timesheets.labels.non_project_activity" />
              )}
            </TextWrapperWithEllipsis>
          </SecondaryTextWrapper>
        </NoteTypeWrapper>
      )}
      <CommentWrapper>
        <div className={styles.content__left}>
          <Avatar
            size="default"
            shape="square"
            className={styles.avatar}
            src={user?.photoUrl}
            name={user?.firstName}
          />
          <span className={styles.user}>
            {user && fullName(user)}
            {/* <span className="separator" /> */}
          </span>
          <span className={styles.relative_time}>
            <RelativeTime time={moment(note.createdAt)} />
          </span>
        </div>
        <ParagraphStyled>
          <span
            // TODO: render RTE? @asimkt
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line react/no-danger, @typescript-eslint/naming-convention
              __html: note.noteText,
            }}
          />
        </ParagraphStyled>
      </CommentWrapper>
    </NoteWrapper>
  );
};
